import { useMemo } from 'react';

type Vars = Record<string, any>;

function wrapNumber(n: number) {
    return {
        value: n, points: n, valueOf() {
            return n;
        }, toString() {
            return String(n);
        },
    };
}

function extractNumeric(x: any): number | null {
    if (typeof x === 'number') {
        return x;
    }
    if (x && typeof x === 'object') {
        const raw = x.value ?? x.points;
        if (raw != null) {
            const n = Number(raw);
            return Number.isFinite(n) ? n : null;
        }
    }
    return null;
}

function normalize(v: any): any {
    const num = extractNumeric(v);
    if (num != null) {
        return wrapNumber(num);
    }
    if (Array.isArray(v)) {
        return v.map(normalize);
    }
    if (v && typeof v === 'object') {
        const out: Record<string, any> = {};
        for (const k of Object.keys(v)) out[k] = normalize(v[k]);
        return out;
    }
    return v;
}

const VALID_ID = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

function preprocess(code: string): string {
    let s = code.trim();
    if ((
        s.startsWith('"') && s.endsWith('"')
    ) || (
        s.startsWith('\'') && s.endsWith('\'')
    )) {
        s = s.slice(1, -1);
    }
    s = s.replace(/{{\s*([^}]+)\s*}}/g, '$1').trim();
    const isFuncExpr = /^(?:async\s*)?(?:function\b|\(\s*[^)]*\s*\)\s*=>|\(\s*\)\s*=>|[A-Za-z_$][A-Za-z0-9_$]*\s*=>)/.test(s);
    if (isFuncExpr) {
        return s;
    }
    const needsBlock = /\b(?:const|let|var|return|if|for|while|switch|function)\b/.test(s) || /[;{}]/.test(s);
    return needsBlock ? `() => { ${s} }` : `() => (${s})`;
}

const useFormulaRunner = () => {
    return useMemo(() => {
        return function runFormulaSync<T = unknown>(fnCode: string, rawData: Vars): T | '-' {
            try {
                const cleaned = preprocess(fnCode);
                const normalized = Object.fromEntries(Object.entries(rawData).map(([ k, v ]) => [ k, normalize(v) ]));
                const validEntries = Object.entries(normalized).filter(([ k ]) => VALID_ID.test(k));
                const argNames = validEntries.map(([ k ]) => k).concat('Math');
                const argValues = validEntries.map(([ , v ]) => v).concat(Math);
                const prelude =
                    `var window, document, XMLHttpRequest, fetch, WebSocket, localStorage, sessionStorage, process, require, module, exports, globalThis, Function;`;

                const body = prelude + '\n' +
                    'const __f = (' + cleaned + ');\n' +
                    'return __f();';

                let exec: Function;
                try {
                    // eslint-disable-next-line no-new-func
                    exec = new Function(...argNames, body);
                } catch {
                    return '-';
                }
                try {
                    return exec(...argValues) as T;
                } catch {
                    return '-';
                }
            } catch {
                return '-';
            }
        };
    }, []);
};

export default useFormulaRunner;
